import * as React from 'react'
import { buildLogSearchRedirectionLink } from '@mulesoft/monitoring-center-components/lib/utils/LogSearchRedirectionHelper'
import { LogPointsConfiguration, Props as ConfigurationProps } from './LogPointsConfiguration/LogPointsConfiguration'
import { LogPointsTopBar, Props as TopBarProps } from '../common/LogPointsTopBar/LogPointsTopBar'
import { LogPointsSidebar, Props as SidebarProps } from './LogPointsSidebar/LogPointsSidebar'
import { LogPointCategory, LogPointType } from '../../util/LoggingPolicyConfigurations'
import { LogPointStates } from '../../util/LogPointsService'
import { LogPointsType } from '../../LogPointsPage/model'
import Spinner from '@mulesoft/anypoint-components/lib/Spinner'
import './LogPointsAPIsBody.css'

export type ExternalProps = {
  organizationId: string
  environmentId: string
  resourceId: string
  versionId: string
  isSaveButtonDisabled: boolean
}
export type ValueProps = ExternalProps & {
  state: LogPointStates
  isLoadingLogPoints: boolean
  isSaving: boolean
  hasSavedYet: boolean
  applicationFullDomain?: string
  isRuntimeVerifiable?: boolean
  error?: any
}

export type EventProps = {
  save: (props: ExternalProps, logPoints: LogPointStates) => void
  getRuntimeVerifiable: (organizationId: string, environmentId: string, versionId: string) => void
  getApplicationFullDomain: (organizationId: string, environmentId: string, versionId: string) => void
  fetchLogPoints: (environmentId: string, versionId: string) => void
  resetError: () => void
}

export type Props = ValueProps & EventProps

export type State = {
  logPoints: LogPointStates
  selectedType: LogPointType
  selectedCategory: LogPointCategory
}

class LogPointsAPIsBody extends React.Component<Props, State> {

  private static logPointsHasDiff(externalLogPoints: LogPointStates, internalLogPoints: LogPointStates) {
    return (externalLogPointKey: string) => {
      const externalLogPoint = externalLogPoints[externalLogPointKey]
      const internalLogPoint = internalLogPoints[externalLogPointKey]
      const externalLogPointEnabled = externalLogPoint.enabled
      const internalLogPointEnabled = internalLogPoint && internalLogPoint.enabled

      return externalLogPointEnabled !== internalLogPointEnabled
    }
  }

  private static getTopBarMessageHref(isRuntimeVerifiable?: boolean, applicationFullDomain?: string): string | undefined {
    const queryParams = isRuntimeVerifiable ? { origin: 'log-points' } : { origin: 'log-points-unverifiable' }
    const logsLinkProps = applicationFullDomain ? {key: 'appId', value: applicationFullDomain, queryParams} : undefined
    const messageHref = logsLinkProps ? buildLogSearchRedirectionLink(logsLinkProps) : undefined

    return messageHref
  }

  constructor(props: Props) {
    super(props)

    this.state = {
      logPoints: this.props.state,
      selectedType: LogPointType.Request,
      selectedCategory: LogPointCategory.Header
    }

    this.getTopBarProps = this.getTopBarProps.bind(this)
    this.getSidebarProps = this.getSidebarProps.bind(this)
    this.getConfigurationProps = this.getConfigurationProps.bind(this)

    this.save = this.save.bind(this)
    this.hasChanges = this.hasChanges.bind(this)
    this.onLogPointActivate = this.onLogPointActivate.bind(this)
    this.onToggleAll = this.onToggleAll.bind(this)
    this.onSidebarView = this.onSidebarView.bind(this)

    props.getApplicationFullDomain(props.organizationId, props.environmentId, props.versionId)
    props.getRuntimeVerifiable(props.organizationId, props.environmentId, props.versionId)
    props.fetchLogPoints(props.environmentId, props.versionId)
  }

  getTopBarProps(): TopBarProps {
    const {applicationFullDomain, isSaving, hasSavedYet, error, isRuntimeVerifiable, isSaveButtonDisabled} = this.props

    return {
      isSaving: isSaving,
      disabledButton: isSaveButtonDisabled,
      hasChanges: this.hasChanges(),
      hasSavedYet: hasSavedYet,
      error: error,
      save: this.save,
      messageHref: LogPointsAPIsBody.getTopBarMessageHref(isRuntimeVerifiable, applicationFullDomain),
      type: LogPointsType.API
    }
  }

  getSidebarProps(): SidebarProps {
    return {
      state: this.state.logPoints,
      selectedType: this.state.selectedType,
      selectedCategory: this.state.selectedCategory,
      onClick: this.onSidebarView,
      onToggleAll: this.onToggleAll
    }
  }

  getConfigurationProps(): ConfigurationProps {
    return {
      state: this.state.logPoints,
      logPointType: this.state.selectedType,
      logPointCategory: this.state.selectedCategory,
      onLogPointActivate: this.onLogPointActivate,
      onToggleAll: this.onToggleAll
    }
  }

  save() {
    this.props.save(this.props, this.state.logPoints)
  }

  hasChanges() {
    const externalLogPoints = this.props.state
    const internalLogPoints = this.state.logPoints

    if (!externalLogPoints || !internalLogPoints) return false

    const keys = Object.keys(externalLogPoints)
    const foundDiff = keys.find(LogPointsAPIsBody.logPointsHasDiff(externalLogPoints, internalLogPoints))

    return foundDiff !== undefined
  }

  onLogPointActivate(id: string) {
    if (this.props.error) {
      this.props.resetError()
    }

    const logPoints = this.state.logPoints
    const aLogPoint = logPoints[id]

    if (aLogPoint !== undefined) {
      this.setState({logPoints: {...logPoints, [id]: {...aLogPoint, enabled: !aLogPoint.enabled}}})
    }
  }

  onToggleAll(toggleNewVal: boolean) {
    if (this.props.error) {
      this.props.resetError()
    }

    const newLogPoints: LogPointStates = {}

    Object
      .keys(this.state.logPoints)
      .forEach((key) => {
        const logPoint = this.state.logPoints[key]
        const matchesSelectedType = logPoint.type === this.state.selectedType
        const matchesSelectedCategory = logPoint.category === this.state.selectedCategory
        const matches = matchesSelectedType && matchesSelectedCategory
        newLogPoints[key] = matches ? {...logPoint, enabled: toggleNewVal} : {...logPoint}
      })

    this.setState({logPoints: newLogPoints})
  }

  onSidebarView(type: LogPointType, category: LogPointCategory) {
    this.setState({selectedType: type, selectedCategory: category})
  }

  componentWillReceiveProps(nextProps: Readonly<Props>) {
    const resettingError = this.props.error && nextProps.error === undefined

    if (!nextProps.isLoadingLogPoints && !nextProps.isSaving && !resettingError) {
      this.setState({logPoints: nextProps.state})
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, prevContext: any): void {
    const {organizationId, environmentId, resourceId, versionId, error} = this.props

    if (prevProps.resourceId !== resourceId || prevProps.versionId !== versionId) {
      if (error) {
        this.props.resetError()
      }

      this.props.getApplicationFullDomain(organizationId, environmentId, versionId)
      this.props.getRuntimeVerifiable(organizationId, environmentId, versionId)
      this.props.fetchLogPoints(environmentId, versionId)
    }
  }

  render() {
    const topBarProps = this.getTopBarProps()
    const sidebarProps = this.getSidebarProps()
    const configurationProps = this.getConfigurationProps()

    return (
      <div className="log-points-apis-body" data-test-id="log-points-apis-body">
        {
          !this.props.isLoadingLogPoints &&
          <div className="log-points-page-main-content-container" data-test-id="log-points-page-main-content-container">
              <div className="log-points-page-main-content-container-header" data-test-id="log-points-page-main-content-container-header">
                  <LogPointsTopBar {...topBarProps}/>
              </div>
              <div className="log-points-page-main-content-container-body" data-test-id="log-points-page-main-content-container-body">
                  <LogPointsSidebar {...sidebarProps}/>
                  <LogPointsConfiguration {...configurationProps}/>
              </div>
          </div>
        }

        {
          this.props.isLoadingLogPoints &&
          <div className="log-points-page-loading" data-test-id="log-points-page-loading">
              <Spinner/>
          </div>
        }
      </div>
    )
  }
}

export default LogPointsAPIsBody
